import { createTheme } from '@mui/material/styles';
import { color, typographyLineHeight, typographySize } from './Styles';

const darkInputColor = color.primary_medium;

const commonSettings = {
    typography: {
        fontFamily: 'Archivo, sans-serif',
    },
    components: {
        // add fontWeight 400 to all input labels and switch labels for better readability
        MuiFormControlLabel: {
            styleOverrides: {
                root: {
                    fontWeight: 400,
                },
            },
        },
        MuiInputLabel: {
            styleOverrides: {
                root: {
                    fontSize: '1rem',
                    fontWeight: 400,
                },
            },
        },

        /**
         * Helper text is a footnote to its field, not a line of body copy: small, tight, and pulled up
         * against the input's bottom edge, with its left edge on the input's text so it reads as attached.
         * Error helpers keep their own colour — `.Mui-error` outranks this rule.
         */
        MuiFormHelperText: {
            styleOverrides: {
                root: {
                    fontSize: '0.6875rem',
                    lineHeight: 1.35,
                    margin: '0.2rem 0 0 0.75rem',
                },
            },
        },

        MuiCheckbox: {
            styleOverrides: {
                indeterminate: {
                    color: 'red',
                },
            },
        },
        MuiButtonBase: {
            defaultProps: {
                disableRipple: true,
            },
        },
        MuiDialog: {
            styleOverrides: {
                paper: {
                    borderRadius: '0.5rem',
                },
            },
        },
        MuiTooltip: {
            styleOverrides: {
                tooltip: {
                    fontSize: typographySize.font_sm,
                    lineHeight: typographyLineHeight.font_xs,
                },
            },
            defaultProps: {
                enterDelay: 800,
                leaveDelay: 200,
                enterNextDelay: 400,
            },
        },
        MuiRadio: {
            defaultProps: {
                disableRipple: true,
            },
        },
    },
};

export const muiLightTheme = createTheme({
    ...commonSettings,
    palette: {
        mode: 'light',
        text: {
            primary: color.primary_dark,
        },
        primary: {
            main: color.secondary,
            contrastText: color.border,
        },
        secondary: {
            main: color.tertiary,
        },
        error: {
            main: color.error,
        },
        background: {
            paper: color.bg_light,
        },
        info: {
            light: color.secondary_medium, // switch icon
            main: color.secondary,
            dark: color.bg_light,
            contrastText: color.bg_light,
        },
    },
    components: {
        ...commonSettings.components,

        MuiInputLabel: {
            styleOverrides: {
                root: {
                    ...commonSettings.components.MuiInputLabel.styleOverrides.root,
                    color: color.secondary_medium,
                },
            },
        },
        MuiFormHelperText: {
            styleOverrides: {
                root: {
                    ...commonSettings.components.MuiFormHelperText.styleOverrides.root,
                    color: color.secondary_medium,
                },
            },
        },
        MuiInputBase: {
            styleOverrides: {
                input: {
                    fontSize: '0.75rem',
                    '&::placeholder': {
                        color: color.secondary_light,
                        fontWeight: 400,
                        fontSize: '0.75rem',
                        opacity: 1,
                    },
                },
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                notchedOutline: {
                    borderColor: '#706FA0',
                },
            },
        },
        MuiTooltip: {
            styleOverrides: {
                tooltip: {
                    ...commonSettings.components.MuiTooltip.styleOverrides.tooltip,
                    backgroundColor: color.secondary_medium_light,
                    color: color.primary_dark,
                },
                arrow: {
                    color: color.bg_light,
                },
            },
        },
    },
});

export const muiDarkTheme = createTheme({
    ...commonSettings,
    palette: {
        mode: 'dark',
        text: {
            primary: color.bg_light,
        },
        primary: {
            main: color.accent_light,
            contrastText: color.primary_light,
        },
        secondary: {
            main: color.tertiary,
        },
        error: {
            main: color.error_light,
        },
        background: {
            paper: color.primary_dark,
        },
        info: {
            light: color.accent_dark, // switch icon
            main: color.accent,
            dark: color.primary_dark,
            contrastText: color.bg_light,
        },
    },
    components: {
        ...commonSettings.components,
        MuiInputLabel: {
            styleOverrides: {
                root: {
                    ...commonSettings.components.MuiInputLabel.styleOverrides.root,
                    color: darkInputColor,
                },
            },
        },
        MuiFormHelperText: {
            styleOverrides: {
                root: {
                    ...commonSettings.components.MuiFormHelperText.styleOverrides.root,
                    color: darkInputColor,
                },
            },
        },
        MuiInputBase: {
            styleOverrides: {
                input: {
                    fontSize: '0.75rem',
                    '&::placeholder': {
                        color: darkInputColor,
                        fontWeight: 400,
                        fontSize: '0.75rem',
                        opacity: 1,
                    },
                },
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                notchedOutline: {
                    borderColor: darkInputColor,
                },
            },
        },
        MuiTooltip: {
            styleOverrides: {
                tooltip: {
                    ...commonSettings.components.MuiTooltip.styleOverrides.tooltip,
                    backgroundColor: color.primary_dark,
                },
                arrow: {
                    color: color.primary_dark,
                },
            },
        },
    },
});
